import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@mlm/ui';
import { useCartStore } from '../storefront/lib/cart-store';
import { productToAddLine } from '../storefront/lib/cart-map';
import { toDisplay, useBrand } from '../storefront/lib/hooks';
import { ProductCard } from './product-card';
import { getMe, getStorefront } from '../lib/api';

/**
 * The activation-kit grid for a member with NO active slot — the one body
 * shared by the dashboard's "Pick your activation kit" card and the
 * Browse-kits `KitPickerDialog`. The two used to be near-identical copies
 * whose Buy Now behaviour drifted (`add` vs `addOnly`); this is the merge.
 *
 * No slotId ON PURPOSE — do not "fix" this to pass the active slot. This
 * only ever renders for a member with no active slot, so there is nothing to
 * price against and the server falls back to base price. The bare
 * `['storefront']` key is likewise deliberate: it must NOT share a cache
 * entry with the slot-keyed `['storefront', slotId]` used everywhere else.
 *
 * The server already applies the campaign kit scope for a slotless caller and
 * `createOrderInTx` is the authoritative gate (`product_not_in_package`); the
 * `registeredLinkKitIds` filter here is belt-and-braces on top of that, and
 * also covers a campaign whose tags changed after the catalog was cached.
 */
export function useActivationKits(enabled = true) {
  const storefrontQ = useQuery({
    queryKey: ['storefront'],
    queryFn: () => getStorefront(),
    enabled,
  });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled });

  const allKits = (storefrontQ.data?.products ?? []).filter((p) => p.isKit);
  // Replicated-link packages: scope the lead to the activation kits tagged to
  // the campaign page they signed up through. null = no restriction (show all).
  const linkKitIds = meQ.data?.registeredLinkKitIds ?? null;
  const kits = linkKitIds ? allKits.filter((p) => linkKitIds.includes(p.id)) : allKits;

  return { kits, allKits, isLoading: storefrontQ.isLoading || meQ.isLoading };
}

export function KitGrid({
  enabled = true,
  onPicked,
  gridClassName = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3',
}: {
  /** Gate the queries (e.g. only while the hosting dialog is open). */
  enabled?: boolean;
  /** Called when Buy Now hands off to /checkout (dialogs close themselves). */
  onPicked?: () => void;
  /** The caller's grid track classes (the dialog adds a scroll cap). */
  gridClassName?: string;
}) {
  const navigate = useNavigate();
  const brand = useBrand();
  const lines = useCartStore((s) => s.lines);
  const addToCart = useCartStore((s) => s.add);
  const addOnly = useCartStore((s) => s.addOnly);
  const { kits, allKits, isLoading } = useActivationKits(enabled);

  // Normalized rows for the cart mapper, keyed by id so the card's own raw row
  // still drives the display. Kits can never have variations
  // (`kit_cannot_have_variants`), so a kit line needs no variant id.
  const displayById = useMemo(() => new Map(allKits.map((p) => [p.id, toDisplay(p)])), [allKits]);
  const inCartQty = (productId: string) =>
    Object.values(lines).find((l) => l.productId === productId)?.qty ?? 0;
  const addKit = (productId: string) => {
    const d = displayById.get(productId);
    if (d) addToCart(productToAddLine(d, brand, { qty: 1 }));
  };

  function buyNow(productId: string) {
    const d = displayById.get(productId);
    if (!d) return;
    // `addOnly` rather than `add`: this is a one-thing purchase, so whatever is
    // already in the cart must not be billed alongside the activation kit.
    addOnly(productToAddLine(d, brand, { qty: 1 }));
    onPicked?.();
    // Always /checkout. A VIRTUAL kit gets the instant-pay treatment from there
    // — checkout redirects an all-virtual cart to the payment page itself — so
    // this must NOT special-case it. Deciding here as well would be a second
    // copy of that rule, and a PHYSICAL kit sent straight to payment would skip
    // the address or pickup branch it still needs.
    navigate('/checkout');
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-56" />
        ))}
      </div>
    );
  }

  if (kits.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No {brand.activationKitLabelPlural} are available yet. Please check back, or contact your
        sponsor.
      </p>
    );
  }

  return (
    <div className={gridClassName}>
      {kits.map((p) => (
        <ProductCard
          key={p.id}
          view={{
            id: p.id,
            name: p.name,
            sku: p.sku,
            description: p.description,
            images: p.images,
            isKit: p.isKit,
            featured: p.featured,
            pv: p.pv,
            price: p.priceEffective,
            priceBefore: p.priceBase,
            discountPct: Number(p.appliedDiscountPct),
          }}
          basePath="/product"
          inCart={inCartQty(p.id)}
          onAdd={() => addKit(p.id)}
          onBuyNow={() => buyNow(p.id)}
        />
      ))}
    </div>
  );
}
